import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that displays a marquee.
 *
 * Intentionally "simple and stupid": a continuously running strip driven purely by
 * a CSS keyframe animation. There is NO drag, NO hover slow-down, NO pointer
 * interaction of any kind — the component is `pointer-events: none` in CSS. JS is
 * only responsible for (1) cloning enough content to fill the viewport for a
 * seamless loop and (2) computing the animation duration from the strip width.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  /** Tracks the viewport width the layout was last built for. */
  #lastWidth = 0;

  async connectedCallback() {
    super.connectedCallback();

    const { content } = this.refs;
    if (content.firstElementChild?.children.length === 0) return;

    if (this.#isStaticDesktop()) {
      this.setAttribute('data-disabled', '');
      return;
    }

    // Measure only after images have loaded. On iOS especially, images decode
    // after the first layout pass, so measuring item widths too early yields the
    // wrong number of copies and a wrong speed — which is what produced the
    // scattered cards / excessive gaps on mobile.
    await this.#whenImagesReady();
    if (!this.isConnected) return;

    this.#build();

    // CLS fix: the CSS animation is gated on [data-ready]. Set it now that content
    // is cloned and --marquee-speed is set, so the animation begins from frame 0
    // (translate 0) rather than snapping to its end-state during the unset-speed gap.
    this.setAttribute('data-ready', '');

    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
  }

  #isStaticDesktop() {
    return this.hasAttribute('data-static-desktop') && window.matchMedia('(min-width: 750px)').matches;
  }

  /**
   * Resolves once every <img> in the marquee has finished loading (or errored).
   * @returns {Promise<unknown>}
   */
  #whenImagesReady() {
    const pending = /** @type {HTMLImageElement[]} */ ([...this.querySelectorAll('img')]).filter(
      (img) => !img.complete
    );

    if (pending.length === 0) return Promise.resolve();

    return Promise.all(
      pending.map(
        (img) =>
          new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          })
      )
    );
  }

  #build() {
    this.#lastWidth = window.innerWidth;
    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 20;
    const marqueeWidth = this.offsetWidth;
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return speed;
  }

  #handleResize = debounce(() => {
    if (this.#isStaticDesktop()) {
      this.setAttribute('data-disabled', '');
      return;
    }

    // Ignore resizes that don't change the viewport WIDTH. On iOS, scrolling
    // shows/hides the browser chrome which fires `resize` with a changed height
    // only — rebuilding there reset the animation to frame 0 and made the strip
    // visibly jump back to the first card ("always gets back to Peter Pan").
    if (window.innerWidth === this.#lastWidth) return;
    this.#lastWidth = window.innerWidth;

    this.removeAttribute('data-disabled');

    const { content } = this.refs;
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed();
  }, 250);

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const wrapper = content.firstElementChild;

    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = wrapper.cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth =
      content.firstElementChild instanceof HTMLElement ? content.firstElementChild.offsetWidth : 1;

    return marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
